/**
 * V2DataSourceService
 *
 * Provides variable-based data sources for the condition editor.
 * Uses VariableResolver from v2-variable-utils to find bound variables,
 * then enriches them with metadata for UI rendering.
 *
 * Replaces V1's DataSourceFactory which worked with screen data paths.
 *
 * @module v2-condition-editor
 */

import type { FlowDocument } from '@segmently/schema-models/v2';
import type { V2DataSource } from '../types/v2-condition-editor.types';
import type { BoundVariable } from '@modules/v2-variable-utils/types/variable-utils.types';
import { variableResolver } from '@modules/v2-variable-utils/services/VariableResolver';

export class V2DataSourceService {
  /**
   * Get all variables available as condition data sources for a given step.
   *
   * Flow:
   * 1. Uses VariableResolver.findAvailableVariables() for BFS predecessor traversal
   * 2. Maps BoundVariable[] → V2DataSource[] with enriched metadata
   *
   * @param flowDocument - Full FlowDocument with variables and screens
   * @param sourceStepId - The step whose outgoing route is being edited
   * @returns V2DataSource[] sorted by step order (upstream → current)
   */
  getAvailableDataSources(
    flowDocument: FlowDocument,
    sourceStepId: string,
  ): V2DataSource[] {
    const boundVariables = variableResolver.findAvailableVariables(
      flowDocument,
      sourceStepId,
    );

    return boundVariables.map((bv) => this.toDataSource(bv));
  }

  /**
   * Find a specific data source by variable ID.
   */
  findDataSource(
    flowDocument: FlowDocument,
    sourceStepId: string,
    variableId: string,
  ): V2DataSource | undefined {
    const sources = this.getAvailableDataSources(flowDocument, sourceStepId);
    return sources.find((s) => s.variableId === variableId);
  }

  /**
   * Convert a BoundVariable to a V2DataSource with enriched metadata.
   * Global variables (no boundScreenIds) will have undefined boundByStepId/Name.
   */
  private toDataSource(bv: BoundVariable): V2DataSource {
    const { variable } = bv;

    const ds: V2DataSource = {
      variableId: variable.id,
      variableName: variable.name,
      variableType: variable.type as V2DataSource['variableType'],
      boundByStepId: bv.boundByStepId,
      boundByStepName: bv.boundByStepName,
    };

    // Enrich with type-specific metadata
    if (variable.options && variable.options.length > 0) {
      ds.options = variable.options;
    }
    if (variable.min !== undefined) ds.min = variable.min;
    if (variable.max !== undefined) ds.max = variable.max;
    if (variable.minDate !== undefined) ds.minDate = variable.minDate;
    if (variable.maxDate !== undefined) ds.maxDate = variable.maxDate;

    return ds;
  }
}

/** Singleton instance */
export const v2DataSourceService = new V2DataSourceService();
